import React from 'react';
import PropTypes from 'prop-types';
import BreakpointObserver, { sizes } from '@decisiv/breakpoint-observer';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import Button from '@decisiv/ui-components/lib/components/Button';
import { Badge, H2 } from '@decisiv/ui-components';
import Lock from '@decisiv/iconix/lib/components/Lock';

import usePasswordReset from './hooks/usePasswordReset';
import {
  ButtonsContainer,
  PasswordResetContainer,
} from './components/styledComponents';

function PasswordReset({ csrfToken, translations, backLoginClick }) {
  // The breakpoints object defines the minimum widths for specific breakpoint names.
  const breakpoints = {
    [sizes.SM]: 300,
    [sizes.MD]: 600,
  };

  const {
    identity,
    setIdentity,
    requestResetPassword,
    requestingResetPassword,
  } = usePasswordReset(csrfToken, translations.resetPasswordSuccess);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (identity.trim() && !requestingResetPassword) {
        requestResetPassword();
        setIdentity('');
      }
    }
  };

  return (
    <BreakpointObserver breakpoints={breakpoints}>
      {({
        breakpoint, // The currently active breakpoint. Defaults to `sizes.XS`.
        dimensions, // An object containing the `height` & `width` of the observed DOM element.
        observedElementRef, // The ref must be assigned to the DOM element you want to observe.
      }) => (
        <div ref={observedElementRef}>
          <PasswordResetContainer>
            <Badge
              aria-label="lock badge"
              color="information"
              icon={Lock}
              size="large"
              palette="bright"
            />
            <H2 style={{ marginTop: '10px' }}>
              {translations.resetPasswordTitle}
            </H2>
            <P color="alaskanHusky" style={{ textAlign: 'center' }}>
              {translations.resetPasswordInstructions}
            </P>
            <ButtonsContainer>
              <Flex
                flexDirection="row"
                marginBottom={1}
                style={{ width: '100%' }}
              >
                <Flex flex={1}>
                  <TextField
                    required
                    style={{ width: '100%' }}
                    label={translations.usernameOrEmail}
                    name="identity"
                    value={identity}
                    onChange={(e) => {
                      setIdentity(e.target.value);
                    }}
                    onKeyPress={handleKeyPress}
                  />
                </Flex>
              </Flex>

              <Button
                text={translations.sendResetLink}
                onClick={() => {
                  requestResetPassword();
                  setIdentity('');
                }}
                disabled={!identity.trim() || requestingResetPassword}
              />

              <Button
                text={translations.backToLogin}
                variant="ghost"
                size="small"
                onClick={backLoginClick}
                style={{ marginTop: '25px' }}
              />
            </ButtonsContainer>
          </PasswordResetContainer>
        </div>
      )}
    </BreakpointObserver>
  );
}

PasswordReset.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  translations: PropTypes.objectOf(PropTypes.string),
  backLoginClick: PropTypes.func,
};

PasswordReset.defaultProps = {
  translations: {},
  backLoginClick: () => {},
};

export default PasswordReset;
